import React, { useCallback, useEffect, useState } from "react";
import { Role, getRoleFeatures } from "../constants/role";

import { services } from "../dependencies";
import { parseJwt } from "../utils/browser";

import Cookies from "js-cookie";
import { v4 as uuidv4 } from "uuid";

export const validateSession = (user) => {
  return user?.exp > Math.floor(Date.now() / 1000);
};

const validateCSRole = (role) => {
  if (
    role === Role.CS_ADMIN.id ||
    role === Role.CS_MANAGER.id ||
    role === Role.CS_AUTHOR.id
  ) {
    return true;
  } else {
    throw new Error("User unauthorized");
  }
};

const createSessionId = (
  userId,
  exp,
  cookieName = "socket_identity",
  cookieConfig
) => {
  const sessionId = uuidv4();
  const cookieValue = `${userId}:${sessionId}`;

  Cookies.set(cookieName, cookieValue, {
    expires: new Date(exp * 1000),
    path: "/",
    secure: true,
    ...cookieConfig,
  });

  return sessionId;
};

export const SessionContext = React.createContext({});

export default ({ userCache, tokenCache, children }) => {
  const [currentUser, setCurrentUser] = useState();
  const [token, setToken] = useState();
  const [role, setRole] = useState();
  const [orgCode, setOrgCode] = useState();
  const [features, setFeatures] = useState();
  const [error, setError] = useState();
  const [isMount, setIsMount] = useState();
  const [activeTab, setActiveTab] = useState();

  const { authenticationService } = services;

  const performLogout = useCallback(() => {
    setCurrentUser();
    tokenCache.remove();
    userCache.remove();
    setToken();
    setRole();
    setOrgCode();
    setFeatures();
  }, [tokenCache, userCache]);

  const isLoggedIn = currentUser ? validateSession(currentUser) : false;

  const contextValue = {
    currentUser,
    token,
    orgCode,
    features,
    isLoggedIn,
    error,
    role,
    activeTab,
    setActiveTab,
    login: async ({ username, password }) => {
      try {
        setError(false);
        const user = await authenticationService.login(username, password);
        setValidUser(user);
      } catch (error) {
        console.error(error);
        tokenCache.remove();
        userCache.remove();
        setError(error);
      }
    },
    logout: performLogout,
  };

  useEffect(() => {
    if (!currentUser) return;

    const interval = setInterval(() => {
      if (!validateSession(currentUser)) {
        performLogout();
        window.location.href = "/login";
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [currentUser, performLogout]);

  useEffect(() => {
    const user = userCache?.get();
    const decodedToken = user && parseJwt(user?.token);

    if (user && !user.sessionId) {
      const sessionId = createSessionId(user.id, user.exp);
      user.sessionId = sessionId;
      userCache.set(user, { exp: user.exp });
    }

    setCurrentUser(user);
    setToken(tokenCache?.get());
    setRole(decodedToken?.act);
    setOrgCode(user?.organizationCode);
    setFeatures(getRoleFeatures(decodedToken?.act));
    setIsMount(true);
  }, []);

  const setValidUser = (user) => {
    const currentToken = parseJwt(user.token);
    const currentUser = { exp: currentToken.exp, ...user };
    const role = currentToken?.act;
    validateCSRole(role);
    tokenCache.set(user.token, { exp: currentToken.exp });
    userCache.set(currentUser, { exp: currentToken.exp });
    setCurrentUser(currentUser);
    setToken(currentToken);
    setRole(role);
    setOrgCode(currentUser?.organizationCode);
    setFeatures(getRoleFeatures(role));
  };

  return (
    isMount && (
      <SessionContext.Provider value={contextValue}>
        {children}
      </SessionContext.Provider>
    )
  );
};
